import React from 'react';
import FilterIcon from './FilterIcon';
import { FILTER_ALL_KEY, FilterOption, getOptionKeys, normaliseSelection } from './selection';

export interface FilterCheckboxListProps {
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  emptyText?: string;
  ariaLabel?: string;
  disabled?: boolean;
  disabledText?: string;
}

const FilterCheckboxList: React.FC<FilterCheckboxListProps> = ({
  options,
  selected,
  onChange,
  emptyText = 'No options available',
  ariaLabel,
  disabled = false,
  disabledText,
}) => {
  const visibleOptions = options.filter((option) => option.key !== FILTER_ALL_KEY);
  const optionKeys = getOptionKeys(options);

  const commit = (next: string[]) => {
    if (disabled) return;
    onChange(normaliseSelection(next, optionKeys));
  };

  const isChecked = (key: string): boolean => {
    if (selected.includes(FILTER_ALL_KEY)) return true;
    return selected.includes(key);
  };

  const handleOptionChange = (key: string, checked: boolean) => {
    if (disabled) return;

    if (selected.includes(FILTER_ALL_KEY)) {
      commit(optionKeys.filter((optionKey) => optionKey !== key));
      return;
    }

    if (checked) {
      commit([...selected.filter((optionKey) => optionKey !== FILTER_ALL_KEY), key]);
      return;
    }

    commit(selected.filter((optionKey) => optionKey !== key));
  };

  return (
    <div
      className={`sf-check-list${disabled ? ' is-disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
    >
      {!disabled && visibleOptions.length > 0 ? (
        <div className="sf-check-list__actions">
          <button type="button" className="sf-link-button" onClick={() => onChange([FILTER_ALL_KEY])}>
            All
          </button>
          <button type="button" className="sf-link-button" onClick={() => onChange([])}>
            None
          </button>
        </div>
      ) : null}

      {disabled && disabledText ? <p className="sf-muted">{disabledText}</p> : null}

      {visibleOptions.map((option) => (
        <div className="sf-check-row" key={option.key}>
          <label className="sf-check">
            <input
              type="checkbox"
              checked={isChecked(option.key)}
              disabled={disabled}
              onChange={(event) => handleOptionChange(option.key, event.target.checked)}
            />
            <span className="sf-check__box" aria-hidden="true">
              <FilterIcon name="check" size={12} />
            </span>
            <span className="sf-check__label">
              {option.value}
              {option.meta ? <span className="sf-check__meta">{option.meta}</span> : null}
            </span>
          </label>
        </div>
      ))}

      {!disabled && visibleOptions.length === 0 && emptyText ? <p className="sf-muted">{emptyText}</p> : null}
    </div>
  );
};

export default FilterCheckboxList;
